import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";

export default function ImportSheetPage({ onBack }) {
  const fileRef    = useRef();
  const [wb,       setWb]       = useState(null);
  const [fileName, setFileName] = useState("");
  const [sheet,    setSheet]    = useState("");
  const [preview,  setPreview]  = useState(null);
  const [busy,     setBusy]     = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState("");

  const readSheet = (workbook, sheetName) => {
    const ws   = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    setPreview(data.slice(0, 6));
    return data;
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(""); setResult(null); setPreview(null); setWb(null);
    try {
      const buf      = await f.arrayBuffer();
      const workbook = XLSX.read(buf, { type: "array" });
      setWb(workbook);
      setFileName(f.name);
      const first = workbook.SheetNames[0] || "";
      setSheet(first);
      if (first) readSheet(workbook, first);
    } catch (err) {
      setError("ფაილის წაკითხვა ვერ მოხერხდა: " + err.message);
    }
    // reset input so the same file can be re-selected
    e.target.value = "";
  };

  const handleSheetChange = (name) => {
    setSheet(name);
    setResult(null);
    if (wb) readSheet(wb, name);
  };

  const handleImport = async () => {
    if (!wb || !sheet) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const ws   = wb.Sheets[sheet];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (data.length === 0) { setError("ფურცელი ცარიელია."); return; }

      const colCount = Math.max(...data.map((r) => r.length));
      const padded   = data.map((r) => {
        const row = [...r];
        while (row.length < colCount) row.push("");
        return row;
      });

      await Excel.run(async (ctx) => {
        const ws2  = ctx.workbook.worksheets.getActiveWorksheet();
        const rng  = ws2.getRangeByIndexes(0, 0, padded.length, colCount);
        rng.values = padded;
        await ctx.sync();
      });

      setResult({ rows: data.length, cols: colCount });
    } catch (err) {
      setError("შეცდომა: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="taskpane-root taskpane-root--boq"
      style={{ padding: "20px 16px" }}
      data-no-translate="true"
    >
      <button className="link" onClick={onBack}>← უკან</button>
      <h2 style={{ marginTop: 16, marginBottom: 4 }}>Excel-ის შემოტანა</h2>
      <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 20, lineHeight: 1.5 }}>
        .xlsx ფაილის არჩევის შემდეგ, მისი ფურცლის მონაცემები
        ჩაიწერება <strong>მიმდინარე Excel ფურცელში</strong>.
      </p>

      {/* ── File picker ── */}
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleFile}
      />
      <button
        className="primary"
        style={{ width: "100%", marginBottom: 16 }}
        onClick={() => fileRef.current?.click()}
      >
        {fileName ? `📂 ${fileName}` : "📂 ფაილის არჩევა (.xlsx / .xls)"}
      </button>

      {/* ── Sheet selector ── */}
      {wb && wb.SheetNames.length > 1 && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>
            ფურცელი:
          </label>
          <select
            value={sheet}
            onChange={(e) => handleSheetChange(e.target.value)}
            style={{
              width: "100%", padding: "7px 10px", borderRadius: 6, fontSize: 12,
              border: "1px solid rgba(148,163,184,0.3)",
              background: "var(--bg-secondary)", color: "var(--text-primary)",
            }}
          >
            {wb.SheetNames.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      )}

      {/* ── Preview ── */}
      {preview && preview.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
            გადახედვა — პირველი {preview.length} სტრიქონი:
          </div>
          <div style={{
            overflowX: "auto",
            border: "1px solid rgba(148,163,184,0.2)",
            borderRadius: 6, maxHeight: 160,
          }}>
            <table style={{ fontSize: 10, borderCollapse: "collapse", width: "100%" }}>
              <tbody>
                {preview.map((row, ri) => (
                  <tr
                    key={ri}
                    style={{
                      borderBottom: "1px solid rgba(148,163,184,0.12)",
                      background: ri === 0 ? "rgba(6,182,212,0.08)" : "transparent",
                    }}
                  >
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          padding: "4px 8px",
                          color: ri === 0 ? "#7dd3fc" : "var(--text-primary)",
                          fontWeight: ri === 0 ? 600 : "normal",
                          maxWidth: 120, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                      >
                        {String(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error  && <div className="msg-error"   style={{ marginBottom: 12 }}>{error}</div>}
      {result && (
        <div className="msg-success" style={{ marginBottom: 12 }}>
          ✓ {result.rows} სტრიქონი, {result.cols} სვეტი წარმატებით ჩაიწერა!
        </div>
      )}

      {/* ── Import button ── */}
      {wb && sheet && !result && (
        <button
          className="primary"
          onClick={handleImport}
          disabled={busy}
          style={{ width: "100%" }}
        >
          {busy ? "იტვირთება..." : `↓ შემოტანა — "${sheet}"`}
        </button>
      )}

      {result && (
        <button
          style={{
            width: "100%", padding: "8px", borderRadius: 6, fontSize: 12,
            border: "1px solid rgba(148,163,184,0.3)", cursor: "pointer",
            background: "transparent", color: "var(--text-muted)",
          }}
          onClick={() => { setWb(null); setFileName(""); setSheet(""); setPreview(null); setResult(null); }}
        >
          სხვა ფაილის შემოტანა
        </button>
      )}
    </div>
  );
}
